import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import { apiRequest } from "@mhj/shared/api";
import { useAuth } from "@mhj/shared/auth";
import DocumentUpload from "../components/DocumentUpload";
import TrackPicker from "../components/TrackPicker";
import {
  Card,
  Field,
  Steps,
  buttonClass,
  inputClass,
  secondaryButtonClass,
} from "../lib/ui";
import { docLabel } from "../lib/docs";
import type { Application, EmpowermentConfig } from "../lib/types";

type Draft = {
  trackId: string;
  gender: "male" | "female" | "";
  dateOfBirth: string;
  nin: string;
  ward: string;
  address: string;
  educationLevel: string;
  employmentStatus: string;
  maritalStatus: string;
  dependants: number;
  incomeBand: string;
  hasDisability: boolean;
  disabilityDetails: string;
  trackDetail: string;
  bankName: string;
  bankAccountNumber: string;
  bankAccountName: string;
};

const LOCAL_DRAFT_KEY = "mhj_empowerment_draft";

const EMPTY: Draft = {
  trackId: "",
  gender: "",
  dateOfBirth: "",
  nin: "",
  ward: "",
  address: "",
  educationLevel: "",
  employmentStatus: "",
  maritalStatus: "",
  dependants: 0,
  incomeBand: "",
  hasDisability: false,
  disabilityDetails: "",
  trackDetail: "",
  bankName: "",
  bankAccountNumber: "",
  bankAccountName: "",
};

const EDUCATION = [
  ["none", "No formal education"],
  ["primary", "Primary school"],
  ["secondary", "Secondary school"],
  ["vocational", "Vocational or technical"],
  ["tertiary", "University or polytechnic"],
];
const EMPLOYMENT = [
  ["unemployed", "Not working"],
  ["self_employed", "Self-employed"],
  ["employed", "Employed"],
  ["student", "Student"],
  ["farmer", "Farmer"],
];
const MARITAL = [
  ["single", "Single"],
  ["married", "Married"],
  ["widowed", "Widowed"],
  ["divorced", "Divorced"],
];
const INCOME = [
  ["none", "No regular income"],
  ["under_30k", "Under ₦30,000 a month"],
  ["30k_100k", "₦30,000 to ₦100,000 a month"],
  ["100k_plus", "Over ₦100,000 a month"],
];

export default function Apply() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState<Draft>(() => {
    // Restore anything typed before the server draft existed.
    try {
      const saved = localStorage.getItem(LOCAL_DRAFT_KEY);
      return saved ? { ...EMPTY, ...(JSON.parse(saved) as Partial<Draft>) } : EMPTY;
    } catch {
      return EMPTY;
    }
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Mirror every keystroke locally. Cheap, and it means an accidental refresh
  // on a slow phone does not send someone back to the first question.
  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_DRAFT_KEY, JSON.stringify(draft));
    } catch {
      // Private browsing, or storage disabled. Not worth interrupting anyone.
    }
  }, [draft]);

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ["empowerment", "config"],
    queryFn: () => apiRequest<EmpowermentConfig>("/empowerment/config"),
  });

  const { data: mine, isLoading: mineLoading } = useQuery({
    queryKey: ["empowerment", "me"],
    queryFn: () => apiRequest<{ application: Application | null }>("/empowerment/me"),
  });

  const application = mine?.application ?? null;
  const tracks = config?.tracks ?? [];
  const selectedTrack = useMemo(
    () => tracks.find((t) => t.id === draft.trackId) ?? null,
    [tracks, draft.trackId],
  );

  // Rehydrate a saved draft so a dropped connection does not lose the work.
  useEffect(() => {
    if (!application) return;
    setDraft({
      trackId: application.trackId,
      gender: application.gender,
      dateOfBirth: application.dateOfBirth,
      nin: application.nin ?? "",
      ward: application.ward,
      address: application.address ?? "",
      educationLevel: application.educationLevel,
      employmentStatus: application.employmentStatus,
      maritalStatus: application.maritalStatus,
      dependants: application.dependants,
      incomeBand: application.incomeBand,
      hasDisability: application.hasDisability,
      disabilityDetails: application.disabilityDetails ?? "",
      trackDetail: application.trackDetail ?? "",
      bankName: application.bankName ?? "",
      bankAccountNumber: application.bankAccountNumber ?? "",
      bankAccountName: application.bankAccountName ?? "",
    });
  }, [application]);

  const saveDraft = useMutation({
    mutationFn: () =>
      apiRequest<{ application: Application }>("/empowerment/start", {
        method: "POST",
        withCsrf: true,
        data: {
          trackId: draft.trackId,
          gender: draft.gender,
          dateOfBirth: draft.dateOfBirth,
          nin: draft.nin.trim() || null,
          ward: draft.ward,
          address: draft.address.trim() || null,
          educationLevel: draft.educationLevel,
          employmentStatus: draft.employmentStatus,
          maritalStatus: draft.maritalStatus,
          dependants: Number(draft.dependants),
          incomeBand: draft.incomeBand,
          hasDisability: draft.hasDisability,
          disabilityDetails: draft.disabilityDetails.trim() || null,
          trackDetail: draft.trackDetail.trim() || null,
          bankName: draft.bankName.trim() || null,
          bankAccountNumber: draft.bankAccountNumber.trim() || null,
          bankAccountName: draft.bankAccountName.trim() || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["empowerment", "me"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const submit = useMutation({
    mutationFn: () =>
      apiRequest<{ application: Application }>("/empowerment/submit", {
        method: "POST",
        withCsrf: true,
        data: { applicationId: application?.id },
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["empowerment", "me"] });
      try {
        localStorage.removeItem(LOCAL_DRAFT_KEY);
      } catch {
        // Nothing to clean up if storage was unavailable to begin with.
      }
      toast.success(`Application submitted. Your ID is ${result.application.applicationNo}`);
      navigate("/dashboard");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (configLoading || mineLoading) {
    return <Card><p className="text-slate-500">Loading…</p></Card>;
  }

  if (!config?.cycle) {
    return (
      <Card title="Applications are closed">
        <p className="text-slate-600">
          {config?.message ?? "The next round opens soon."}
        </p>
      </Card>
    );
  }

  // G8: the server refuses an unverified phone, so say why here rather than
  // letting them fill in four steps first.
  if (user && !user.isPhoneVerified) {
    return (
      <Card title="Confirm your phone number first">
        <p className="text-slate-600">
          We need to know we can reach you before you apply. It takes a moment.
        </p>
        <Link
          to={`/verify-phone?phone=${encodeURIComponent(user.phone ?? "")}`}
          className={`${buttonClass} mt-4`}
        >
          Confirm my number
        </Link>
      </Card>
    );
  }

  if (application?.submittedAt) {
    return (
      <Card title="You have already applied">
        <p className="text-slate-600">
          Your application <strong>{application.applicationNo}</strong> is with us.
          You cannot change it now.
        </p>
        <Link to="/dashboard" className={`${buttonClass} mt-4`}>
          See my application
        </Link>
      </Card>
    );
  }

  const validateStep = (n: number) => {
    const next: Record<string, string> = {};
    if (n === 1) {
      if (!draft.gender) next.gender = "Select one";
      if (!draft.dateOfBirth) next.dateOfBirth = "Enter your date of birth";
      if (draft.nin && !/^\d{11}$/.test(draft.nin.trim())) next.nin = "A NIN is 11 digits";
      if (!draft.educationLevel) next.educationLevel = "Select one";
      if (!draft.employmentStatus) next.employmentStatus = "Select one";
      if (!draft.maritalStatus) next.maritalStatus = "Select one";
      if (!draft.incomeBand) next.incomeBand = "Select one";
    }
    if (n === 2 && !draft.ward) next.ward = "Select your ward";
    if (n === 3) {
      if (!draft.trackId) next.trackId = "Choose what you are applying for";
      if (selectedTrack?.detailRequired && draft.trackDetail.trim().length < 5) {
        next.trackDetail = "Please answer this question";
      }
      if (selectedTrack?.requiresBankDetails) {
        if (!draft.bankName.trim()) next.bankName = "Enter your bank";
        if (!/^\d{10}$/.test(draft.bankAccountNumber.trim()))
          next.bankAccountNumber = "An account number is 10 digits";
        if (!draft.bankAccountName.trim()) next.bankAccountName = "Enter the account name";
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const goNext = async () => {
    if (!validateStep(step)) return;
    // The server draft requires a trackId, which is not chosen until step 3,
    // so persist to the server from there on. Steps 1 and 2 are held in
    // localStorage meanwhile, so a refresh or a dropped connection never
    // costs the applicant their answers.
    if (step >= 3) {
      try {
        await saveDraft.mutateAsync();
      } catch {
        return;
      }
    }
    setStep((s) => Math.min(4, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const requiredDocs = selectedTrack?.requiredDocs ?? [];
  const uploaded = new Set((application?.documents ?? []).map((d) => d.docType));
  const missingDocs = requiredDocs.filter((d) => !uploaded.has(d));

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Steps current={step} total={4} />
        <p className="text-sm text-slate-500">
          Step {step} of 4 ·{" "}
          {["About you", "Where you live", "What you need", "Your documents"][step - 1]}
        </p>
      </div>

      {step === 1 && (
        <Card title="About you" description="This helps us understand who the programme is reaching.">
          <div className="space-y-4">
            <Field label="Gender" required error={errors.gender}>
              <div className="grid grid-cols-2 gap-2">
                {(["female", "male"] as const).map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, gender: value }))}
                    className={`rounded-2xl border px-4 py-3 text-base capitalize transition ${
                      draft.gender === value
                        ? "border-blue-600 bg-blue-50 font-medium text-blue-800"
                        : "border-slate-300 text-slate-700"
                    }`}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Date of birth" required error={errors.dateOfBirth}>
              <input
                type="date"
                className={inputClass}
                value={draft.dateOfBirth}
                onChange={(e) => setDraft((d) => ({ ...d, dateOfBirth: e.target.value }))}
                max={new Date().toISOString().slice(0, 10)}
              />
            </Field>

            <Field
              label="NIN"
              error={errors.nin}
              hint="Optional now, but needed before any money or equipment is handed over."
            >
              <input
                className={inputClass}
                value={draft.nin}
                onChange={(e) => setDraft((d) => ({ ...d, nin: e.target.value.replace(/\D/g, "").slice(0, 11) }))}
                inputMode="numeric"
                placeholder="11 digits"
              />
            </Field>

            <Field label="Highest education" required error={errors.educationLevel}>
              <select
                className={inputClass}
                value={draft.educationLevel}
                onChange={(e) => setDraft((d) => ({ ...d, educationLevel: e.target.value }))}
              >
                <option value="">Select…</option>
                {EDUCATION.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>

            <Field label="What do you do now?" required error={errors.employmentStatus}>
              <select
                className={inputClass}
                value={draft.employmentStatus}
                onChange={(e) => setDraft((d) => ({ ...d, employmentStatus: e.target.value }))}
              >
                <option value="">Select…</option>
                {EMPLOYMENT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>

            <Field label="Marital status" required error={errors.maritalStatus}>
              <select
                className={inputClass}
                value={draft.maritalStatus}
                onChange={(e) => setDraft((d) => ({ ...d, maritalStatus: e.target.value }))}
              >
                <option value="">Select…</option>
                {MARITAL.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>

            <Field label="How many people depend on you?" hint="Children or others you support.">
              <input
                type="number"
                min={0}
                max={50}
                className={inputClass}
                value={draft.dependants}
                onChange={(e) => setDraft((d) => ({ ...d, dependants: Number(e.target.value) }))}
              />
            </Field>

            <Field label="Monthly income" required error={errors.incomeBand}>
              <select
                className={inputClass}
                value={draft.incomeBand}
                onChange={(e) => setDraft((d) => ({ ...d, incomeBand: e.target.value }))}
              >
                <option value="">Select…</option>
                {INCOME.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>

            <div className="rounded-2xl bg-slate-50 p-4">
              <label className="flex items-start gap-3 text-sm text-slate-800">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5"
                  checked={draft.hasDisability}
                  onChange={(e) => setDraft((d) => ({ ...d, hasDisability: e.target.checked }))}
                />
                <span>
                  I live with a disability
                  <span className="mt-0.5 block text-xs text-slate-500">
                    Telling us helps the training centre plan. It does not count against you.
                  </span>
                </span>
              </label>
              {draft.hasDisability && (
                <input
                  className={`${inputClass} mt-3`}
                  value={draft.disabilityDetails}
                  onChange={(e) => setDraft((d) => ({ ...d, disabilityDetails: e.target.value }))}
                  placeholder="Anything the centre should know (optional)"
                />
              )}
            </div>
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card
          title="Where you live"
          description="This programme serves Lapai Local Government only."
        >
          <div className="space-y-4">
            <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <strong className="text-slate-900">{config.state} State</strong> ·{" "}
              <strong className="text-slate-900">{config.lga} Local Government</strong>
            </div>

            <Field label="Ward" required error={errors.ward}>
              <select
                className={inputClass}
                value={draft.ward}
                onChange={(e) => setDraft((d) => ({ ...d, ward: e.target.value }))}
              >
                <option value="">Select your ward…</option>
                {config.wards.map((ward) => (
                  <option key={ward} value={ward}>{ward}</option>
                ))}
              </select>
            </Field>

            <Field label="Address" hint="Optional. A landmark is fine.">
              <input
                className={inputClass}
                value={draft.address}
                onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
                placeholder="Near the central mosque, Evuti"
              />
            </Field>
          </div>
        </Card>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <Card title="What do you need?" description="Choose one. You can only apply for one thing per round.">
            <TrackPicker
              categories={config.categories}
              tracks={tracks}
              selectedId={draft.trackId}
              onSelect={(id) => setDraft((d) => ({ ...d, trackId: id }))}
              gender={draft.gender || null}
              dateOfBirth={draft.dateOfBirth || null}
              closesAt={config.cycle.closesAt}
            />
            {errors.trackId && (
              <p className="mt-2 text-xs font-medium text-red-600">{errors.trackId}</p>
            )}
          </Card>

          {selectedTrack && (
            <Card title={selectedTrack.name}>
              <div className="space-y-4">
                <Field
                  label={selectedTrack.detailPrompt}
                  required={selectedTrack.detailRequired}
                  error={errors.trackDetail}
                >
                  <textarea
                    className={`${inputClass} min-h-28`}
                    value={draft.trackDetail}
                    onChange={(e) => setDraft((d) => ({ ...d, trackDetail: e.target.value }))}
                    placeholder="Write as much or as little as you like."
                  />
                </Field>

                {selectedTrack.requiresBankDetails && (
                  <div className="space-y-4 rounded-2xl bg-blue-50 p-4">
                    <p className="text-sm font-medium text-blue-900">
                      This track can award money, so we need somewhere to pay it.
                    </p>
                    <Field label="Bank" required error={errors.bankName}>
                      <input
                        className={inputClass}
                        value={draft.bankName}
                        onChange={(e) => setDraft((d) => ({ ...d, bankName: e.target.value }))}
                        placeholder="First Bank"
                      />
                    </Field>
                    <Field label="Account number" required error={errors.bankAccountNumber}>
                      <input
                        className={inputClass}
                        value={draft.bankAccountNumber}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            bankAccountNumber: e.target.value.replace(/\D/g, "").slice(0, 10),
                          })
                        }
                        inputMode="numeric"
                        placeholder="10 digits"
                      />
                    </Field>
                    <Field label="Account name" required error={errors.bankAccountName}>
                      <input
                        className={inputClass}
                        value={draft.bankAccountName}
                        onChange={(e) => setDraft((d) => ({ ...d, bankAccountName: e.target.value }))}
                        placeholder="As it appears at the bank"
                      />
                    </Field>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          <Card
            title="Your documents"
            description="Photograph each one with your phone. We shrink the picture before sending, so a weak connection is fine."
          >
            {!application ? (
              <p className="text-sm text-slate-600">
                Go back and finish the earlier steps first.
              </p>
            ) : (
              <div className="space-y-3">
                {requiredDocs.map((docType) => (
                  <DocumentUpload
                    key={docType}
                    applicationId={application.id}
                    docType={docType}
                    label={docLabel(docType)}
                    uploaded={uploaded.has(docType)}
                  />
                ))}
              </div>
            )}
          </Card>

          <Card title="Submit">
            {missingDocs.length > 0 ? (
              <p className="text-sm text-amber-800">
                Still needed: {missingDocs.map(docLabel).join(", ")}
              </p>
            ) : (
              <p className="text-sm text-slate-600">
                Everything is here. Once you submit you cannot change your
                application, and we will send your application ID by text message.
              </p>
            )}
            <button
              type="button"
              onClick={() => submit.mutate()}
              disabled={submit.isPending || missingDocs.length > 0 || !application}
              className={`${buttonClass} mt-4`}
            >
              {submit.isPending ? "Submitting…" : "Submit my application"}
            </button>
          </Card>
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row-reverse">
        {step < 4 && (
          <button
            type="button"
            onClick={() => void goNext()}
            disabled={saveDraft.isPending}
            className={buttonClass}
          >
            {saveDraft.isPending ? "Saving…" : "Continue"}
          </button>
        )}
        {step > 1 && (
          <button type="button" onClick={goBack} className={secondaryButtonClass}>
            Back
          </button>
        )}
      </div>
    </div>
  );
}
